import { useTranslation } from "react-i18next";
import { ScrollText } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type TermsItem = {
  title: string;
  body: string;
};

type TermsSection = {
  title: string;
  // Either a plain body, numbered sub-items, or both.
  body?: string;
  items?: TermsItem[];
};

/**
 * The terms are legally binding in Arabic only, so the page always renders
 * the Arabic text (pulled explicitly from the ar bundle) regardless of the
 * app language.
 */
const AR = { lng: "ar" } as const;

export default function Terms() {
  const { t } = useTranslation();
  const sections = t("terms.sections", { returnObjects: true, ...AR }) as TermsSection[];

  return (
    <div className="min-h-screen pt-24 pb-24" dir="rtl">
      {/* Header */}
      <div className="container mx-auto px-4 mb-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 bg-card rounded-2xl shadow-sm border border-border flex items-center justify-center mx-auto mb-6">
            <ScrollText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-display">
            {t("terms.title", AR)}
          </h1>
          <p className="text-base text-muted-foreground">
            {t("terms.subtitle", AR)}
          </p>
        </div>
      </div>

      {/* Intro */}
      <div className="container mx-auto px-4 mb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-muted/60 border border-border rounded-2xl p-6 md:p-8 text-muted-foreground leading-relaxed whitespace-pre-line">
            {t("terms.intro", AR)}
          </div>
        </div>
      </div>

      {/* Sections — collapsed by default so the seven parts scan quickly */}
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <Accordion type="multiple" className="space-y-4">
            {sections.map((section, i) => (
              <AccordionItem
                key={i}
                value={`section-${i}`}
                className="border border-border rounded-2xl bg-card px-5 md:px-6"
              >
                <AccordionTrigger className="text-base md:text-lg font-bold font-display hover:no-underline text-start gap-3">
                  {section.title}
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-6">
                  {section.body && (
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {section.body}
                    </p>
                  )}
                  {section.items && (
                    <div className="space-y-6 mt-2">
                      {section.items.map((item, j) => (
                        <div key={j}>
                          <h3 className="text-sm md:text-base font-semibold mb-2">
                            {item.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                            {item.body}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
